import { useCallback } from 'react';
import { AppearanceTypes, useToasts } from 'react-toast-notifications';

import {
  I18nToastNotificationMessage,
  RemoveToastNotificationHandlerType,
  ShowToastI18nNotificationHandlerType,
  ShowToastNotificationHandlerType,
  ToastNotificationMessage
} from './useToastNotification.types';

import { useTranslate } from '../useTranslate';

interface ToastNotificationOptions {
  appearance?: AppearanceTypes;
  autoDismiss?: boolean;
  message?: ToastNotificationMessage;
  i18nMessage?: I18nToastNotificationMessage;
}

function useToastNotification(options: ToastNotificationOptions = {}) {
  const {
    message,
    i18nMessage,
    appearance = 'success',
    autoDismiss = true
  } = options;

  const { addToast, removeToast } = useToasts();
  const { t } = useTranslate();

  const showToastNotification = useCallback<ShowToastNotificationHandlerType>(
    (toastMessage, toastAppearance, toastId, toastAutoDismiss) =>
      addToast(toastMessage || message, {
        appearance: toastAppearance || appearance,
        autoDismiss: toastAutoDismiss ?? autoDismiss,
        id: toastId
      }),
    [message, appearance, autoDismiss, addToast]
  );

  const showToastI18nNotification =
    useCallback<ShowToastI18nNotificationHandlerType>(
      (i18nToastMessage, toastAppearance, toastId, toastAutoDismiss) =>
        addToast(t(i18nToastMessage || i18nMessage), {
          appearance: toastAppearance || appearance,
          autoDismiss: toastAutoDismiss ?? autoDismiss,
          id: toastId
        }),
      [t, i18nMessage, appearance, autoDismiss, addToast]
    );

  const removeToastNotification =
    useCallback<RemoveToastNotificationHandlerType>(
      (toastId) => removeToast(toastId),
      [removeToast]
    );

  return {
    removeToastNotification,
    showToastNotification,
    showToastI18nNotification
  };
}

export default useToastNotification;
